import { Provider as ProviderV2 } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import { CalendarEvent } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Provider as ProviderV3 } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/provider.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDate } from '@frontend/date';
import { ExtendedAppointment } from '../../hooks';
import { formatDateTime } from '../../utils/date-helpers';

const getAppointmentDate = (event: CalendarEvent, timezone: string) => {
  return formatDateTime(`${event.startDate} ${event.startTime}`, {
    fromTimezone: 'UTC',
    toTimezone: timezone,
  });
};

export const convertEventTimesFromUTC = (event: CalendarEvent, locationTimeZone: string) => {
  const startDateTime = formatDateTime(`${event.startDate} ${event.startTime}`, {
    fromTimezone: 'UTC',
    toTimezone: locationTimeZone,
  });
  const endDateTime = formatDateTime(`${event.endDate} ${event.endTime}`, {
    fromTimezone: 'UTC',
    toTimezone: locationTimeZone,
  });

  return {
    ...event,
    startDate: formatDate(startDateTime, 'YYYY-MM-DD'),
    startTime: formatDate(startDateTime, 'HH:mm:ss'),
    endDate: formatDate(endDateTime, 'YYYY-MM-DD'),
    endTime: formatDate(endDateTime, 'HH:mm:ss'),
  };
};

const checkIsOverlappingEvent = (
  event: Partial<Record<'startDate' | 'endDate' | 'startTime' | 'endTime', string>>
): boolean => {
  const { startDate, endDate, startTime, endTime } = event;
  return !!(startDate && endDate && startTime && endTime && startDate !== endDate && endTime !== '00:00:00');
};

export const convertV3CalendarEventToV2Appointment = (
  event: CalendarEvent,
  locationTimezoneMap?: SchedulerV3.Helpers.LocationTimezoneMap
): ExtendedAppointment => {
  const locationId = event.locationId ?? '';

  const locationTimezone = locationTimezoneMap?.[locationId]?.timezone;
  const convertedEvent = locationTimezone ? convertEventTimesFromUTC(event, locationTimezone) : event;
  const isOverlapping = checkIsOverlappingEvent(convertedEvent);
  const operatoryName =
    event.operatoryDetails?.displayName || event.operatoryDetails?.externalName || event.details?.opNa || '';

  const appointment = {
    id: event.id ?? '',
    locationIds: locationId ? [locationId] : [], // Revisit
    locationId: locationId,
    // NOTE startDate: V2 format is YYYY-MM-DDTHH:mm:ssZ. When a timezone is present we're returning format MM/DD/YYYY hh:mm A
    start: locationTimezoneMap?.[locationId]
      ? getAppointmentDate(event, locationTimezoneMap[locationId]?.timezone)
      : dayjs(`${event.startDate} ${event.startTime}`).toString(),
    // type: Previous code was using eventType as a fallback to populate the type field. EventType is not the appointment type. An appointment is created with the eventType set to 'APPOINTMENT'. type: event.details?.rTyNa === appointment type
    type: event.details?.rTyNa ?? '',
    appointmentTypeId: event.referenceTypeId ?? '',
    duration: event.details?.dur,
    externalStatus: event.attendeeStatus,
    // Set the practitioner name to an empty string, if the provider is unassigned
    // TODO: Update when event.details?.orNa is empty from API
    practitionerName: event.organizerId === '' ? '' : event.details?.orNa,
    providerIds: event.organizerId ? [event.organizerId] : [],
    operatory: operatoryName,
    person: {
      firstName: event.attendeeDetails?.firstName,
      lastName: event.attendeeDetails?.lastName,
      preferredName: event.attendeeDetails?.preferredName,
      personId: event.attendeeDetails?.personId,
      gender: event.attendeeDetails?.gender,
      status: event.attendeeDetails?.status,
      birthDate: event.attendeeDetails?.birthdate,
      householdId: event.attendeeDetails?.householdId,
      mobilePhone: event.attendeeDetails?.contactInfo?.find((contact) => contact.type === ContactType_Enum.PHONE_MOBILE)
        ?.destination,
      homePhone: event.attendeeDetails?.contactInfo?.find((contact) => contact.type === ContactType_Enum.PHONE_HOME)
        ?.destination,
      workPhone: event.attendeeDetails?.contactInfo?.find((contact) => contact.type === ContactType_Enum.PHONE_WORK)
        ?.destination,
    },
    createdBySourceId: event.sourceTenantId,
    workstationIds: event.operatoryId ? [event.operatoryId] : [],
    isNonIntegratedSource: !event.isIntegrated,
    statusOfficeView: event.attendeeStatus,
    referenceId: event.referenceId ?? '',
    isOverlapping,
    displayOptions: {
      primaryColor: event.displayOptions?.primaryColor,
      backgroundColor: event.displayOptions?.backgroundColor,
      textColor: event.displayOptions?.textColor,
    },
  };

  return appointment;
};

export const convertV3CalendarEventsToV2Appointments = (
  events: CalendarEvent[],
  locationTimezoneMap?: SchedulerV3.Helpers.LocationTimezoneMap
): ExtendedAppointment[] => events.map((event) => convertV3CalendarEventToV2Appointment(event, locationTimezoneMap));

export const convertV3ProviderToV2Provider = (practitioners: ProviderV3[]): ProviderV2[] =>
  practitioners.map((practitioner) => ({
    id: practitioner.id,
    firstName: practitioner.firstName,
    lastName: practitioner.lastName,
    publicDisplayName: practitioner.displayName || '',
    locationId: practitioner.locationId,
    resourceName: practitioner.externalName,
    externalId: practitioner.externalId,
    publicDisplayImage: practitioner?.profile?.picUrl,
    createdAt: practitioner.createdAt,
    updatedAt: practitioner.updatedAt,
    publicDisplayDescription: practitioner.profile?.bio || '',
    sourceId: practitioner.sourceTenantId,
    isActive: !practitioner.isArchived,
  }));
